import { jsPDF } from 'jspdf';
import { Button, Paragraph } from '@/components/atoms';
import { Sheet, SheetContent, SheetHeader } from '@/components/atoms';
import { ArrowLeftIcon } from 'lucide-react';
import type { Sale, Order, OrderCustomer } from '@/types';

import SellytLogoSvg from '@/assets/svg/sellyt-logo.svg?url';

interface ReceiptSheetProps {
  showReceipt: boolean;
  setShowReceipt: (show: boolean) => void;
  selectedSale: Sale | null;
  selectedOrder?: Order | null;
  onClose: () => void;
  onShare: () => void;
}

const getOrdinal = (n: number) => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] ?? s[v] ?? s[0]);
};

const formatReceiptDate = (dateStr: string | null | undefined) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '—';
  const day = d.getDate();
  const month = d.toLocaleDateString('en-GB', { month: 'long' });
  const year = d.getFullYear();
  return `${getOrdinal(day)} ${month} ${year}`;
};

const parseMoney = (amount: string | number | null | undefined): number => {
  if (amount == null || amount === '') return 0;
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  return Number.isNaN(num) ? 0 : num;
};

const getOrderCostBreakdown = (order: Order | null) => {
  const currency = order?.currency ?? 'NGN';

  const itemsSubtotal =
    order?.orderItems?.reduce(
      (sum, item) => sum + (item.productPriceAt ?? 0) * (item.quantity ?? 0),
      0
    ) ?? 0;

  const itemCost = parseMoney(order?.subtotal) || itemsSubtotal;
  const shippingCost = parseMoney(order?.shippingFee);
  const total = parseMoney(order?.total);
  const fees = Math.max(0, total - itemCost - shippingCost);

  return {
    currency,
    itemCost,
    shippingCost,
    fees,
    total: total || itemCost + shippingCost + fees,
  };
};

const formatMoney = (
  amount: string | number | null | undefined,
  currency = 'NGN'
) => {
  if (amount == null || amount === '') return '—';
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (Number.isNaN(num)) return '—';
  const code = currency === 'NGN' ? '₦' : currency;
  return `${code}${num.toLocaleString()}`;
};

/** PDF-safe money format (Helvetica has no ₦); use currency code so it shows. */
const formatMoneyPdf = (
  amount: string | number | null | undefined,
  currency = 'NGN'
) => {
  if (amount == null || amount === '') return '—';
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (Number.isNaN(num)) return '—';
  const code = currency && currency !== 'NGN' ? currency : 'NGN';
  return `${code} ${num.toLocaleString()}`;
};

const PAYMENT_METHOD_LABELS: Record<string, string> = {
  card: 'Card',
  bank_transfer: 'Bank transfer',
  bank: 'Bank transfer',
  ussd: 'USSD',
  qr: 'QR Code',
  mobile_money: 'Mobile Money',
  paystack: 'Paystack',
};

const getPaymentMethodLabel = (order: Order): string => {
  const tx = order.transaction?.[0] as
    | { paymentProvider?: string; paymentMethod?: string }
    | undefined;
  const provider = tx?.paymentProvider;
  if (provider) {
    const key = String(provider).toLowerCase().replace(/\s+/g, '_');
    return (
      PAYMENT_METHOD_LABELS[key] ??
      provider.charAt(0).toUpperCase() + provider.slice(1)
    );
  }
  const channel = (() => {
    const meta = order.metadata;
    if (typeof meta !== 'object' || meta === null) return null;
    const pd = (meta as { paymentData?: { channel?: string } }).paymentData;
    return pd?.channel ? String(pd.channel) : null;
  })();
  const fromTx = tx?.paymentMethod ?? channel ?? '';
  if (!fromTx) return '—';
  const key = fromTx.toLowerCase().replace(/\s+/g, '_');
  return (
    PAYMENT_METHOD_LABELS[key] ??
    fromTx.charAt(0).toUpperCase() + fromTx.slice(1)
  );
};

const getCustomerDisplayName = (
  customer: OrderCustomer | null | undefined
): string => {
  if (!customer) return '—';
  const first = (customer.firstName ?? '').trim();
  const last = (customer.lastName ?? '').trim();
  if (first || last) return [first, last].filter(Boolean).join(' ');
  return customer.email ?? '—';
};

const getShippingAddress = (order: Order): string | null => {
  const addresses = order.customer?.addresses;
  if (!Array.isArray(addresses) || addresses.length === 0) return null;
  const shipping =
    addresses.find((a) => String(a.type).toUpperCase() === 'SHIPPING') ??
    addresses[0];
  const parts = [
    shipping.addressLine1,
    shipping.addressLine2,
    [shipping.city, shipping.state].filter(Boolean).join(', '),
    shipping.country,
    shipping.zipCode,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : null;
};

/** Draw a dotted horizontal line (jsPDF has no setLineDash, so we draw short segments). */
const drawDottedLine = (
  doc: jsPDF,
  y: number,
  margin: number,
  width: number
) => {
  const dashLen = 2;
  const gapLen = 2;
  doc.setDrawColor(180, 180, 180);
  let x = margin;
  while (x < margin + width) {
    const x2 = Math.min(x + dashLen, margin + width);
    doc.line(x, y, x2, y);
    x = x2 + gapLen;
  }
};

/** Load Sellyt logo as PNG data URL for embedding in PDF (same-origin SVG). */
const loadLogoDataUrl = (): Promise<string | null> =>
  new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          resolve(null);
          return;
        }
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = SellytLogoSvg;
  });

const downloadReceiptPdf = async (
  order: Order | null,
  sale: Sale | null,
  logoDataUrl: string | null
) => {
  const doc = new jsPDF({ format: 'a4', unit: 'mm' });
  const margin = 20;
  const pageWidth = 210 - margin * 2; // A4 width in mm
  let y = 20;
  const lineHeight = 6;
  const labelColumnWidth = 42; // mm reserved for labels so values have space
  const addLine = (
    text: string,
    opts?: { fontSize?: number; bold?: boolean; align?: 'left' | 'center' }
  ) => {
    doc.setFontSize(opts?.fontSize ?? 10);
    doc.setFont('helvetica', opts?.bold ? 'bold' : 'normal');
    const align = opts?.align ?? 'left';
    const x = align === 'center' ? margin + pageWidth / 2 : margin;
    doc.text(text, x, y, { align: align === 'center' ? 'center' : 'left' });
    y += opts?.fontSize ? opts.fontSize * 0.5 + 2 : lineHeight;
  };
  const addRow = (label: string, value: string) => {
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(label, margin, y);
    doc.text(value, margin + pageWidth - 2, y, {
      align: 'right',
      maxWidth: pageWidth - labelColumnWidth - 2,
    });
    y += lineHeight;
  };

  // Sellyt logo at top (downloaded receipt)
  const logoHeight = 12;
  const logoWidth = 32;
  if (logoDataUrl) {
    try {
      doc.addImage(
        logoDataUrl,
        'PNG',
        margin + (pageWidth - logoWidth) / 2,
        y,
        logoWidth,
        logoHeight
      );
      y += logoHeight + 4;
    } catch {
      doc.setTextColor(34, 197, 94);
      addLine('Sellyt', { fontSize: 18, bold: true, align: 'center' });
      y += 2;
      doc.setTextColor(0, 0, 0);
    }
  } else {
    doc.setTextColor(34, 197, 94);
    addLine('Sellyt', { fontSize: 18, bold: true, align: 'center' });
    y += 2;
    doc.setTextColor(0, 0, 0);
  }
  drawDottedLine(doc, y, margin, pageWidth);
  y += 6;

  y += 6;
  doc.setTextColor(0, 0, 0);
  addLine('RECEIPT', { fontSize: 18, align: 'center' });
  drawDottedLine(doc, y, margin, pageWidth);
  y += 4;
  y += 4;
  const storeNamePdf = order?.store?.name ?? 'Order Receipt';
  addLine(storeNamePdf.toUpperCase(), { fontSize: 18, align: 'center' });
  y += 2;
  drawDottedLine(doc, y, margin, pageWidth);
  y += 4;

  const dateSource = order?.completedAt ?? order?.createdAt;
  addRow('Receipt ID:', order?.reference ? `#${order.reference}` : '—');
  addRow('Date:', formatReceiptDate(dateSource));
  y += 4;

  doc.setTextColor(34, 197, 94);
  addLine('Buyer information', { bold: true });
  doc.setTextColor(0, 0, 0);
  drawDottedLine(doc, y, margin, pageWidth);
  y += 5;
  addRow(
    'Buyer Name:',
    order ? getCustomerDisplayName(order.customer) : (sale?.name ?? '—')
  );
  addRow('Contact:', order?.customer?.phoneNumber ?? '—');
  y += 4;

  if (
    order?.fulfillmentType ||
    order?.note ||
    (order && getShippingAddress(order))
  ) {
    doc.setTextColor(34, 197, 94);
    addLine('Fulfilment', { bold: true });
    doc.setTextColor(0, 0, 0);
    drawDottedLine(doc, y, margin, pageWidth);
    y += 5;
    if (order?.fulfillmentType) {
      const type =
        String(order.fulfillmentType).charAt(0).toUpperCase() +
        String(order.fulfillmentType).slice(1);
      addRow('Type:', type);
    }
    if (order && getShippingAddress(order))
      addRow('Address:', getShippingAddress(order) ?? '—');
    if (order?.note) addRow('Note:', order.note);
    y += 4;
  }

  doc.setTextColor(34, 197, 94);
  addLine('Order summary', { bold: true });
  doc.setTextColor(0, 0, 0);
  drawDottedLine(doc, y, margin, pageWidth);
  y += 5;

  const productLabel = order?.orderItems?.length
    ? order.orderItems
        .map((i) => i.product?.name ?? `Product #${i.productId}`)
        .join(', ')
    : (sale?.products ?? '—');
  const qtyTotal =
    order?.orderItems?.reduce((s, i) => s + (i.quantity ?? 0), 0) ?? 0;
  const qtyLabel = qtyTotal === 1 ? '1 Item' : `${qtyTotal} Items`;
  const statusLabel = order?.status
    ? String(order.status).charAt(0).toUpperCase() +
      String(order.status).slice(1)
    : (sale?.status ?? '—');
  const methodLabel = order ? getPaymentMethodLabel(order) : '—';

  addRow('Product(s):', productLabel);
  addRow('Quantity:', qtyLabel);
  addRow('Status:', statusLabel);
  addRow('Method:', methodLabel);
  y += 4;

  const costs = getOrderCostBreakdown(order);
  addRow('Item cost:', formatMoneyPdf(costs.itemCost, costs.currency));
  addRow('Shipping cost:', formatMoneyPdf(costs.shippingCost, costs.currency));
  addRow('Fees:', formatMoneyPdf(costs.fees, costs.currency));
  y += 2;
  doc.setFillColor(240, 253, 244);
  doc.rect(margin, y - 2, pageWidth, 10, 'F');
  doc.setTextColor(34, 197, 94);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text('Total:', margin, y + 4);
  doc.setTextColor(0, 0, 0);
  doc.text(
    formatMoneyPdf(costs.total, costs.currency),
    margin + pageWidth - 2,
    y + 4,
    {
      align: 'right',
      maxWidth: pageWidth - labelColumnWidth - 2,
    }
  );
  y += 12;

  y += 6;
  doc.setFontSize(8);
  doc.setTextColor(128, 128, 128);
  addLine('POWERED BY GOTHRIVE TECHNOLOGIES', { align: 'center' });

  const filename = `receipt-${order?.reference ?? sale?.id ?? 'receipt'}.pdf`;
  doc.save(filename);
};

const handleDownloadReceipt = async (
  order: Order | null,
  sale: Sale | null
) => {
  const logoDataUrl = await loadLogoDataUrl();
  await downloadReceiptPdf(order, sale, logoDataUrl);
};

export const ReceiptSheet = ({
  showReceipt,
  setShowReceipt,
  selectedSale,
  selectedOrder,
  onClose,
  // onShare,
}: ReceiptSheetProps) => {
  const order = selectedOrder ?? null;
  const sale = selectedSale;

  const storeName = order?.store?.name ?? 'Order Receipt';
  const receiptRef = order?.reference ? `#${order.reference}` : '—';
  const dateSource = order?.completedAt ?? order?.createdAt;
  const buyerName = order
    ? getCustomerDisplayName(order.customer)
    : (sale?.name ?? '—');
  const contact = order?.customer?.phoneNumber ?? '—';
  const statusLabel = order?.status
    ? String(order.status).charAt(0).toUpperCase() +
      String(order.status).slice(1)
    : (sale?.status ?? '—');
  const method = order ? getPaymentMethodLabel(order) : '—';
  const productLabel =
    order?.orderItems?.length === 1
      ? (order.orderItems[0].product?.name ??
        `Product #${order.orderItems[0].productId}`)
      : order?.orderItems?.length
        ? order.orderItems
            .map((i) => i.product?.name ?? `#${i.productId}`)
            .join(', ')
        : (sale?.products ?? '—');
  const quantityLabel =
    order?.orderItems?.length === 1
      ? `${order.orderItems[0].quantity} ${order.orderItems[0].quantity === 1 ? 'Item' : 'Items'}`
      : order?.orderItems?.length
        ? `${order.orderItems.reduce((s, i) => s + i.quantity, 0)} Items`
        : '—';
  const costs = getOrderCostBreakdown(order);

  const SectionTitle = ({ children }: { children: React.ReactNode }) => (
    <>
      <div className="border-t border-dashed border-gray-300 my-2 [border-top-style:dotted]" />
      <h3 className="text-green-600 font-semibold text-sm mt-3 mb-2">
        {children}
      </h3>
    </>
  );

  const Row = ({
    label,
    value,
    wrapValue,
  }: {
    label: string;
    value: React.ReactNode;
    wrapValue?: boolean;
  }) => (
    <div
      className={`flex justify-between gap-4 text-sm py-0.5 ${wrapValue ? 'items-start' : 'items-baseline'}`}
    >
      <span className="text-black shrink-0">{label}</span>
      <span
        className={
          wrapValue
            ? 'text-black font-medium text-right break-words min-w-0 max-w-[65%]'
            : 'text-black font-medium text-right shrink-0'
        }
      >
        {value}
      </span>
    </div>
  );

  return (
    <Sheet open={showReceipt} onOpenChange={setShowReceipt}>
      <SheetContent className="w-full sm:max-w-lg flex flex-col p-0">
        <SheetHeader className="px-4 pt-2 pb-3 border-b border-transparent shrink-0">
          <div className="flex items-center justify-between gap-2 pr-8">
            <Button variant="ghost" size="icon" onClick={onClose}>
              <ArrowLeftIcon className="w-4 h-4" />
            </Button>
            <div className="w-10 shrink-0" aria-hidden />
          </div>
        </SheetHeader>

        {(sale || order) && (
          <div className="flex-1 overflow-y-auto px-6 pb-6">
            <h2 className="flex-1 text-center mb-4 text-xl font-bold text-black truncate">
              {storeName}
            </h2>
            {/* Receipt ID & Date */}
            <div className="space-y-0.5 text-left text-sm pt-4">
              <Paragraph className="text-black">
                <span className="font-medium">{receiptRef}</span>
              </Paragraph>
              <Paragraph className="text-black">
                {formatReceiptDate(dateSource)}
              </Paragraph>
            </div>

            {/* Buyer information */}
            <div className="mt-4">
              <SectionTitle>Buyer information</SectionTitle>
              <div className="space-y-1 text-sm">
                <Row label="Buyer Name:" value={buyerName} />
                <Row label="Contact:" value={contact} />
              </div>
            </div>

            {/* Fulfilment */}
            {order &&
              (order.fulfillmentType ||
                order.note ||
                getShippingAddress(order)) && (
                <div className="mt-4">
                  <SectionTitle>Fulfilment</SectionTitle>
                  <div className="space-y-1 text-sm">
                    {order.fulfillmentType && (
                      <Row
                        label="Type:"
                        value={
                          String(order.fulfillmentType)
                            .charAt(0)
                            .toUpperCase() +
                          String(order.fulfillmentType).slice(1)
                        }
                      />
                    )}
                    {getShippingAddress(order) && (
                      <Row
                        label="Address:"
                        value={getShippingAddress(order)}
                        wrapValue
                      />
                    )}
                    {order.note && <Row label="Note:" value={order.note} />}
                  </div>
                </div>
              )}

            {/* Order summary */}
            <div className="mt-4">
              <SectionTitle>Order summary</SectionTitle>
              <div className="space-y-1 text-sm">
                <Row label="Product(s):" value={productLabel} />
                <Row label="Quantity:" value={quantityLabel} />
                <Row
                  label="Status:"
                  value={
                    <span className="inline-flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-600 shrink-0" />
                      {statusLabel}
                    </span>
                  }
                />
                <Row label="Method:" value={method} />
              </div>
            </div>

            <div className="mt-4 space-y-1 text-sm">
              <Row
                label="Item cost:"
                value={formatMoney(costs.itemCost, costs.currency)}
              />
              <Row
                label="Shipping cost:"
                value={formatMoney(costs.shippingCost, costs.currency)}
              />
              <Row
                label="Fees:"
                value={formatMoney(costs.fees, costs.currency)}
              />
            </div>

            <div className="mt-4 bg-green-50 py-3 px-4">
              <div className="flex justify-between items-baseline">
                <span className="text-green-600 font-semibold text-sm">
                  Total:
                </span>
                <span className="font-bold text-black">
                  {formatMoney(costs.total, costs.currency)}
                </span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="mt-6 space-y-3">
              <Button
                className="w-full bg-green-600 hover:bg-green-700 text-white rounded-md"
                onClick={() => handleDownloadReceipt(order, sale ?? null)}
              >
                Download Receipt
              </Button>
            </div>

            {/* Footer */}
            <div className="pt-6 text-center w-full">
              <Paragraph className="text-xs tracking-[0.2em] text-gray-400 text-center">
                POWERED BY GOTHRIVE TECHNOLOGIES
              </Paragraph>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
